import { Injectable } from '@angular/core';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { getSelectedSeason } from 'qp-utility';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { PaginationService } from 'src/app/shared/services/pagination/pagination.service';
import { StorefrontService } from 'src/app/storefront/storefront.service';
import { PlaybackService } from '../playback/playback.service';
import { BookmarkRecord } from 'fl-bookmarks';
import { isLoggedIn } from '../../utils/LoginUtils';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { handleNewCarouselWidth } from '../../utils/storefrontUtils';

export interface EpisodesPage {
  start: number;
  end: number;
  pageNumber: number;
  seriesId: string;
  seasonId: string;
}

interface SeriesResponse {
  seasons: ResourceVm[];
  currentSeason: ResourceVm;
  episodes: ContainerVm;
}

@Injectable({
  providedIn: 'root',
})
export class EpisodesService {
  private episodes?: ContainerVm;
  // PAGINATION VARIABLES
  private seasonEpisodesPagination: EpisodesPage[] = [];
  private episodesPageSize = 1;
  private selectedEpisodesPage = 0;

  private resourceVm: ResourceVm;
  private seasonNumber: number;
  private episodesCount = 0;

  private bookmarks: BookmarkRecord[];

  constructor(
    private readonly storefrontService: StorefrontService,
    private readonly paginationService: PaginationService,
    private readonly configService: ConfigService,
    private playbackService: PlaybackService,
    private readonly localStorage: LocalStorageService
  ) {
    this.episodesPageSize = Number.parseInt(this.configService.applicationConfig.detailsEpisodesPageSize, 10) || 25;
  }

  set pageSize(newPageSize: number) {
    this.episodesPageSize = newPageSize;
  }

  get pagination(): EpisodesPage[] {
    return this.seasonEpisodesPagination;
  }

  get selectedPage(): number {
    return this.selectedEpisodesPage;
  }

  async selectEpisodesPagination(seasonEpisodes: EpisodesPage): Promise<void> {
    try {
      this.selectedEpisodesPage = seasonEpisodes.pageNumber;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const episodeOrder = this.resourceVm?.episodeOrder ?? undefined;
      const sortOrder = episodeOrder === 'ongoing' ? 'desc' : 'asc';
      const episodePaginationData = await this.paginationService.episodesPagination(
        seasonEpisodes.seriesId,
        seasonEpisodes.seasonId,
        'series',
        'episodes',
        (this.selectedEpisodesPage + 1).toString(),
        this.episodesPageSize.toString(),
        this.seasonNumber.toString(),
        sortOrder
      );
      this.episodes.resources = episodePaginationData.episodes;
    } catch {}
  }

  async fetchSeries(resourceVm: ResourceVm): Promise<SeriesResponse | null> {
    this.resourceVm = resourceVm;
    const resourceId = this.resourceVm.type.includes('series') ? this.resourceVm.id : this.resourceVm.seriesId;
    return this.storefrontService
      .fetchSeries(resourceId, undefined, undefined, 'series', 'seasons')
      .then(async (resourcesVm: ResourceVm[]) => {
        const currentSeason = getSelectedSeason(resourcesVm, this.resourceVm);
        this.seasonNumber = currentSeason.seasonNumber;
        const episodes = await this.paginateSerieEpisodes(resourceId, currentSeason.id);
        return {
          seasons: resourcesVm,
          currentSeason,
          episodes,
        };
      })
      .catch((error) => {
        console.error('ERROR', error);
        return null;
      });
  }

  async paginateSerieEpisodes(seriesId: string, seasonId: string): Promise<ContainerVm | null> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const episodeOrder = this.resourceVm?.episodeOrder ?? undefined;
    try {
      await this.getSerieBookmarks(seriesId);
      this.seasonEpisodesPagination = [];
      this.selectedEpisodesPage = 0;
      await this.fetchEpisodes(seriesId, seasonId, 'series', 'episodes');
      const episodesCount = this.episodes.totalItems;
      const totalPageCount = Math.ceil(episodesCount / this.episodesPageSize);

      if (totalPageCount > 1) {
        for (let i = 0; i < totalPageCount; i++) {
          let start: number;
          let end: number;
          if (episodeOrder === 'ongoing') {
            start = this.episodesCount - i * this.episodesPageSize;
            end = this.episodesCount - (i + 1) * this.episodesPageSize + 1;
            if (end < 1) end = 1;
          } else {
            start = i * this.episodesPageSize + 1;
            end = (i + 1) * this.episodesPageSize;
            if (end >= this.episodesCount) end = this.episodesCount;
          }
          this.seasonEpisodesPagination.push({ start, end, pageNumber: i, seriesId, seasonId });
        }
      }
      return this.episodes;
    } catch (_) {}
    return null;
  }

  async getEpisodesPage(seriesId: string, seasonId: string, pageNumber: number): Promise<ContainerVm | null> {
    try {
      await this.fetchEpisodes(seriesId, seasonId, 'series', 'episodes', pageNumber);
      return this.episodes;
    } catch (_) {}
    return null;
  }

  private async fetchEpisodes(
    seriesId: string,
    seasonId: string,
    path: string,
    episodesPath: string,
    pageNumber?: number
  ): Promise<ContainerVm | null> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const episodeOrder = this.resourceVm?.episodeOrder ?? undefined;
    const sortOrder = episodeOrder === 'ongoing' ? 'desc' : 'asc';
    const episodesData = {
      seriesPath: path,
      episodesPath,
      seasonNumber: this.seasonNumber.toString(),
      episodesSort: sortOrder,
    };
    await this.storefrontService
      .fetchEpisodes(seriesId, seasonId, pageNumber?.toString(), this.episodesPageSize.toString(), episodesData)
      .then((containerVm: ContainerVm) => {
        this.episodes = handleNewCarouselWidth(containerVm);
        this.episodesCount = this.episodes.totalItems;
        this.resourceVm.episodesCount = this.episodesCount;
        this.resourceVm.episodes = this.episodes.resources;
        this.getEpisodesBookmarks();

        return this.episodes;
      })
      .catch((e) => {
        console.log('e', e);
        this.episodes = undefined;
      });
    return null;
  }

  private async getSerieBookmarks(seriesId: string) {
    if (isLoggedIn(this.localStorage)) {
      try {
        this.bookmarks = await this.playbackService.bookmarkService.getSeriesBookmarks(seriesId);
      } catch (e) {
        // no bookmarks
      }
    }
  }

  private async getEpisodesBookmarks() {
    if (isLoggedIn(this.localStorage)) {
      try {
        this.episodes = {
          ...this.episodes,
          resources: (this.episodes?.resources ?? []).map((ep) => {
            // Find the bookmark that matches the episode's ID
            const matchingBookmark = this.bookmarks?.find((bookmark) => bookmark.episodeId === ep.id);
            return {
              ...ep,
              offset: matchingBookmark ? Math.ceil(matchingBookmark.offset / (10 * ep.offset)) : 0,
            };
          }),
        };
      } catch (e) {
        // no bookmarks
      }
    }
  }
}
